import React, { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBookOpen,
  faCirclePlay,
  faEnvelope,
  faMagnifyingGlass,
  faMessage,
  faMobileScreenButton,
  faRobot,
} from "@fortawesome/free-solid-svg-icons";
import BroadcastPage from "../Broadcast/BroadcastPage";

const broadcastVideos = [
  "Create a 1-way Broadcast",
  "Create a 2-way Broadcast",
  "Schedule a Broadcast",
  "Import contacts for a Broadcast",
];

export default function BroadcastInformation() {
  const [showBroadcastCreator, setShowBroadcastCreator] = useState(false);
  const [videoSearchTerm, setVideoSearchTerm] = useState("");
  const videoSearchInputRef = useRef(null);
  const normalisedVideoSearchTerm = videoSearchTerm.trim().toLowerCase();
  const filteredBroadcastVideos = broadcastVideos.filter((video) =>
    video.toLowerCase().includes(normalisedVideoSearchTerm)
  );

  return (
    <main className="broadcast-information container-fluid py-4 py-lg-5">
      <div className="row g-4">
        <section className="col-lg-8" aria-labelledby="broadcast-overview-heading">
          <div className="broadcast-information__card card border-0 rounded-4 p-4 p-lg-5 h-100">
            <h2 id="broadcast-overview-heading" className="h3 fw-bold mb-3">About Broadcast</h2>
            <p className="text-secondary">
              Create one-way and two-way messages, select the people you want to reach, and
              decide when each message should be delivered. Use response and delivery outcomes to
              build follow-up processes that keep conversations moving.
            </p>
            <div className="row g-3 mt-1">
              <Feature icon={faMessage} title="Communication types">
                <ul className="broadcast-information__communication-types list-unstyled mb-0 mt-2">
                  <li>1-way SMS</li>
                  <li>2-way SMS</li>
                  <li>1-way email</li>
                  <li>2-way email</li>
                  <li>1-way voice</li>
                  <li>2-way voice</li>
                </ul>
              </Feature>
              <Feature icon={faRobot} title="Smarter message creation">
                Use AI assistance, translation, personalisation, and shortened links.
              </Feature>
              <Feature icon={faMobileScreenButton} title="Flexible delivery">
                Schedule messages and manage delivery or undelivered-message outcomes.
              </Feature>
              <Feature icon={faEnvelope} title="Connected workflows">
                Use replies, delivery events, and APIs to support your communication process.
              </Feature>
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="btn bg-colored-reverse rounded-pill text-white px-4"
                onClick={() => setShowBroadcastCreator(true)}
              >
                Create a broadcast <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </button>
            </div>
          </div>
        </section>

        <aside className="col-lg-4" aria-labelledby="broadcast-help-heading">
          <div className="broadcast-information__card card border-0 rounded-4 p-4 p-lg-5 h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FontAwesomeIcon className="text-purple fs-3" icon={faBookOpen} aria-hidden="true" />
              <h2 id="broadcast-help-heading" className="h3 fw-bold mb-0">Broadcast help</h2>
            </div>
            <p className="text-secondary">
              Visit the help centre for step-by-step guidance and answers to common Broadcast questions.
            </p>
            <a
              href="https://help.uatboomcomms.com/"
              target="_blank"
              rel="noreferrer"
              className="broadcast-information__link fw-bold text-decoration-none"
            >
              Open Broadcast help <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
            </a>
            <div className="broadcast-information__videos mt-4 pt-4">
              <div className="d-flex align-items-start gap-2 mb-3">
                <FontAwesomeIcon className="text-purple fs-4 mt-1" icon={faCirclePlay} aria-hidden="true" />
                <div>
                  <h2 id="broadcast-videos-heading" className="h3 fw-bold mb-1">Broadcast videos</h2>
                  <p className="small text-secondary mb-0">Learn the key steps for creating and managing messages.</p>
                </div>
              </div>
              <form
                className="broadcast-information__video-search input-group input-group-sm mb-3"
                onSubmit={(event) => event.preventDefault()}
              >
                <label className="visually-hidden" htmlFor="broadcast-video-search">
                  Search broadcast videos
                </label>
                <input
                  ref={videoSearchInputRef}
                  id="broadcast-video-search"
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search broadcast videos"
                  value={videoSearchTerm}
                  onChange={(event) => setVideoSearchTerm(event.target.value)}
                />
                <button
                  type="submit"
                  className="broadcast-information__video-search-button btn"
                  aria-label="Search broadcast videos"
                  onClick={() => videoSearchInputRef.current?.focus()}
                >
                  <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                </button>
              </form>
              <ul className="broadcast-information__video-list list-unstyled mb-0">
                {filteredBroadcastVideos.map((video) => (
                  <li key={video}>
                    <a
                      href="https://help.uatboomcomms.com/"
                      target="_blank"
                      rel="noreferrer"
                      className="broadcast-information__video-link d-flex align-items-center justify-content-between rounded-3 p-3 text-decoration-none"
                    >
                      <span>{video}</span>
                      <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
              {normalisedVideoSearchTerm && filteredBroadcastVideos.length === 0 && (
                <p className="broadcast-information__video-search-empty mb-0">No broadcast videos found.</p>
              )}
            </div>
          </div>
        </aside>
      </div>
      {showBroadcastCreator && (
        <section className="broadcast-information__creator mt-4" aria-label="New broadcast">
          <BroadcastPage broadcastUUID="new" broadcastPage="settings" />
        </section>
      )}
    </main>
  );
}

function Feature({ icon, title, children }) {
  return (
    <div className="col-md-6">
      <div className="broadcast-information__feature h-100 rounded-3 p-3">
        <FontAwesomeIcon className="text-purple me-2" icon={icon} aria-hidden="true" />
        <strong>{title}</strong>
        <div className="small text-secondary mt-2">{children}</div>
      </div>
    </div>
  );
}